export type KeyedStore<T> = {
  get: (key: string) => T | undefined;
  set: (key: string, value: T) => void;
  delete: (key: string) => void;
  /** Keys with at least one live subscriber — the closest thing this store has to "mounted". */
  activeKeys: () => IterableIterator<string>;
  /** Every key currently holding a value, listened to or not — the data, not the UI's view of it. */
  entries: () => IterableIterator<[string, T]>;
  subscribe: (key: string, listener: () => void) => () => void;
};

/**
 * A `Map<key, value>` that notifies per-key subscribers on write — backs `useSyncExternalStore`
 * for module state keyed by something other than a component instance (a query key, a row id).
 *
 * `clearOnUnsubscribe`: `true` for an unbounded, per-mount key space (else keys accumulate
 * forever); `false` for a small, stable space where a transient remount must not discard state
 * that only the store's own domain events should clear.
 *
 * `onTeardown` runs only when `clearOnUnsubscribe` tears a key down, for a caller with its own
 * per-key resources (e.g. timers) needing the same cleanup.
 */
export const createKeyedStore = <T>({
  clearOnUnsubscribe,
  onTeardown,
}: {
  clearOnUnsubscribe: boolean;
  onTeardown?: (key: string) => void;
}): KeyedStore<T> => {
  const valueByKey = new Map<string, T>();
  const listenersByKey = new Map<string, Set<() => void>>();

  const notify = (key: string) => {
    for (const listener of listenersByKey.get(key) ?? []) listener();
  };

  return {
    get: (key) => valueByKey.get(key),
    set: (key, value) => {
      valueByKey.set(key, value);
      notify(key);
    },
    delete: (key) => {
      if (valueByKey.delete(key)) notify(key);
    },
    activeKeys: () => listenersByKey.keys(),
    entries: () => valueByKey.entries(),
    subscribe: (key, listener) => {
      let listeners = listenersByKey.get(key);
      if (!listeners) {
        listeners = new Set();
        listenersByKey.set(key, listeners);
      }
      listeners.add(listener);
      return () => {
        listeners?.delete(listener);
        if (!listeners || listeners.size > 0) return;
        listenersByKey.delete(key);
        if (!clearOnUnsubscribe) return;
        valueByKey.delete(key);
        onTeardown?.(key);
      };
    },
  };
};
